/* eslint-disable react/no-children-prop */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-param-reassign */
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import Form from 'components/common/Form';
import { useRootTranslation, useFetch } from 'utils/hooks';
import { fetchCueContainersLight } from 'redux-core/cueContainers/actions';
import { getAllClearancesWithUsesAndDuration, updateClearancesCues } from 'redux-core/clearances/services';
import { FormButton } from 'components/common/Form/Fields';
import { DrawerHeader, Text, Loading, Button } from 'components/styleguide';
import * as Yup from 'yup';
import { concatStrings } from 'utils';
import map from 'ramda/src/map';
import flatten from 'ramda/src/flatten';
import c from 'ramda/src/compose';
import filter from 'ramda/src/filter';
import propOr from 'ramda/src/propOr';
import uniqBy from 'ramda/src/uniqBy';
import prop from 'ramda/src/prop';
import dissoc from 'ramda/src/dissoc';
import equals from 'ramda/src/equals';
import { yupTimeMMSS } from 'utils/forms';
import Clearances from './Clearances';
import enhancer from './enhancer';

/** Fields the drawer can actually edit. Used to send only changed cues on save so the
 * backend processes one cue instead of re-updating every cue of the track on each save. */
const CUE_EDITABLE_FIELDS = [
  'requestedUseType',
  'requestedUseDuration',
  'requestedUseDurationFull',
  'requestedUsageNotes',
  'actualUseType',
  'actualUseDuration',
  'actualUsageNotes',
  'cueContainerIds',
];

const validationSchema = Yup.object().shape({
  cues: Yup.array().of(
    Yup.object().shape({
      requestedUseDuration: yupTimeMMSS,
      requestedUseType: Yup.string().nullable(),
      actualUseDuration: yupTimeMMSS,
      actualUseType: Yup.string().nullable(),
    }),
  ),
});

const getCues = (track) => {
  if (!track?.clearances) return [];
  const cuesRaw = flatten(
    track.clearances.map((clearance) =>
      clearance.containers.map((container) => {
        container.cues.forEach((cue) => {
          cue.qclearClearanceId = clearance.id;
        });
        return container.cues;
      }),
    ),
  );
  const cues = c(
    uniqBy(prop('id')),
    map((cue) => ({
      ...dissoc('cueContainerId', cue),
      /** Each cue belongs to a single container; keep it as a one-element array so the save
       * path (which fans a cue out per container) stays unchanged. */
      cueContainerIds: [cue.cueContainerId],
    })),
  )(cuesRaw);
  return cues;
};

/** Save submits the form (persisting via handleSubmit) and then closes the drawer. It must be a
 * separate control from "Add Usage", which also submits but should keep the drawer open — so the
 * close lives here, not in handleSubmit. */
const SaveButton = ({ onClose }) => {
  const { submitForm, isValid, isSubmitting } = useFormikContext();
  const { t } = useTranslation();
  const handleClick = async () => {
    if (!isValid) return;
    await submitForm();
    onClose();
  };
  return (
    <Button
      size="xs"
      variant="primary"
      disabled={!isValid || isSubmitting}
      isSubmitting={isSubmitting}
      onClick={handleClick}
    >
      {t('global.forms.save')}
    </Button>
  );
};

SaveButton.propTypes = {
  onClose: PropTypes.func.isRequired,
};

const ClearanceUsesAndDurationsDrawer = ({
  name,
  onClose,
  payload: { trackId, clearanceBundleId, onSubmit, readOnly, cueContainerId },
  divisionId,
  ...formConnectedProps
}) => {
  const dispatch = useDispatch();
  const [track, loading, fetch] = useFetch(
    () => trackId && getAllClearancesWithUsesAndDuration({ trackId, clearanceBundleId, activeOnly: true }),
    [],
    {},
  );
  /** Fetch the cue containers (groups) once, here, instead of once per usage row.
   * This is a key performance fix for tracks with many rights/options. */
  const [cueContainers = [], cueContainersLoading] = useFetch(() => dispatch(fetchCueContainersLight()));
  const cues = getCues(track);
  const initialValues = {
    dirty: formConnectedProps.savedValues?.dirty,
    cues: formConnectedProps.savedValues?.cues ?? cues,
  };

  const t = useRootTranslation('drawers.clearanceUsesAndDurations');
  const onCancel = () => {
    onClose();
  };

  const refetch = () => fetch({ trackId, clearanceBundleId });

  const handleSubmit = async ({ cues: cuesRaw }) => {
    /** Only persist cues that actually changed (new, deleted, or an edited field) compared to the
     * server state. Sending the whole array made the backend re-process every cue of the track
     * serially on each save, which is the main cause of the slow updates. */
    const baselineById = new Map(cues.map((cue) => [cue.id, cue]));
    const isNewCue = (cue) => cue.id == null || /newCue/.test(String(cue.id));
    const hasChanged = (cue) => {
      if (cue.deleted || isNewCue(cue)) return true;
      const baseline = baselineById.get(cue.id);
      if (!baseline) return true;
      return CUE_EDITABLE_FIELDS.some((field) => !equals(cue[field], baseline[field]));
    };

    const changedCues = c(
      filter(({ id, deleted }) => !(id === null && deleted)) /** Remove NEW cues that were deleted */,
      flatten,
      map((cue) =>
        cue.cueContainerIds.map((id) => ({
          /** cue.cueContainerIds is an array of the containers the cue is at, create a new cue for each container */
          ...dissoc('cueContainerIds', cue),
          cueContainerId: id,
          actualUseDuration: cue.actualUseDuration || null,
          id: /newCue/.test(cue.id)
            ? null
            : cue.id /** If regex pass, it means it is a NEW cue and shouldn't carry an id */,
        })),
      ),
      filter(hasChanged),
    )(cuesRaw);

    /** Nothing changed (e.g. form dirtied then reverted) — skip the round trip entirely. */
    if (!changedCues.length) return;

    await updateClearancesCues({ trackId, cues: changedCues, divisionId });
    await onSubmit();
    /** Refetch so additions/edits (e.g. the "Add Usage" button, which also submits) show the
     * persisted server state. Closing the drawer is handled by the Save button only. */
    refetch();
  };
  // Blank only on initial load; on refetch keep content mounted so scroll + open Options survive.
  if (loading && !track?.clearances) return <Loading />;

  const presetLabel = (clearance) => clearance.name || '';
  const byPresetName = (a, b) => presetLabel(a).localeCompare(presetLabel(b), undefined, { sensitivity: 'base' });
  const allClearances = propOr([], 'clearances', track);
  const rights = allClearances.filter((clearance) => !clearance.option).sort(byPresetName);
  const options = allClearances.filter((clearance) => clearance.option).sort(byPresetName);

  return (
    <Form
      drawerName={name}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      /** Validate on blur/submit, not on every keystroke. The schema validates the whole cues
       * array, so per-keystroke validation was O(cues) work (plus an extra async re-render) on
       * each character — a major cost on tracks with many clearances. */
      validateOnChange={false}
      enableReinitialize
      initialValues={initialValues}
      readOnly={readOnly}
    >
      <DrawerHeader title={t('title')} hideBackButton onClose={onClose}>
        <FormButton
          alwaysEnabled
          children="global.forms.cancel"
          size="xs"
          variant="destructive"
          onClick={() => onCancel()}
        />
        <SaveButton onClose={onClose} />
      </DrawerHeader>
      <Grid container style={{ paddingBottom: 32 }}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Box mb={3} mt={3}>
            <Text bold variant="h6">
              {track.title}
            </Text>
            <Text>{concatStrings(', ')(track.artists)}</Text>
          </Box>
        </Grid>
        <Grid item xs={12} style={{ marginBottom: 24 }}>
          <Divider />
        </Grid>
        <Clearances
          rights={rights}
          options={options}
          cueContainers={cueContainers}
          cueContainersLoading={cueContainersLoading}
          readOnly={readOnly}
          trackId={trackId}
          qTrackId={track.qwireTracksId}
          cueContainerId={cueContainerId}
          refetch={refetch}
        />
      </Grid>
    </Form>
  );
};

ClearanceUsesAndDurationsDrawer.propTypes = {
  divisionId: PropTypes.number,
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  payload: PropTypes.shape({
    trackId: PropTypes.number,
    clearanceBundleId: PropTypes.number,
    onSubmit: PropTypes.func,
    readOnly: PropTypes.bool,
  }),
  searchForUsers: PropTypes.func.isRequired,
  savedValues: PropTypes.shape({
    dirty: PropTypes.bool,
    cues: PropTypes.array,
  }),
};

export default enhancer(ClearanceUsesAndDurationsDrawer);
